import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api } from "../api"
import { LoadingCard, messageFor } from "../shared"
import { fa, type ForecastWindow, type ProcessSettings, type TasksResponse } from "../process"
import { fiscalYearLabel } from "../shared"
import { CalendarClock } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"

type Division = { id: number; nameFa: string; section: "fs" | "inv"; headId: number | null; isActive: boolean; isCostBucket: boolean }
type Unit = { id: number; nameFa: string; section: "fs" | "inv"; headId: number | null; isActive: boolean; sortOrder: number; reportingEntity: "pishgam" | "faraz" }
type Person = { id: number; nameFa: string; isActive: boolean }

/** Heads come from here: a division head approves their people's splits; a business head gives final approval and owns the forecast. */
export function OrganizationAdmin() {
  const client = useQueryClient()
  const organization = useQuery({ queryKey: ["admin-organization"], queryFn: () => api<{ divisions: Division[]; businessUnits: Unit[] }>("/admin/organization") })
  const people = useQuery({ queryKey: ["admin-users"], queryFn: () => api<Person[]>("/admin/users") })
  const refresh = () => Promise.all(["admin-organization", "organization", "admin-users", "fpa", "pnl-fiscal-year", "me"].map((key) => client.invalidateQueries({ queryKey: [key] })))
  const updateDivision = useMutation({ mutationFn: (division: Division) => api(`/admin/divisions/${division.id}`, { method: "PATCH", body: JSON.stringify({ nameFa: division.nameFa, section: division.section, headId: division.headId, isActive: division.isActive, isCostBucket: division.isCostBucket }) }), onSuccess: refresh })
  const updateUnit = useMutation({ mutationFn: (unit: Unit) => api(`/admin/business-units/${unit.id}`, { method: "PATCH", body: JSON.stringify({ nameFa: unit.nameFa, section: unit.section, headId: unit.headId, isActive: unit.isActive, sortOrder: unit.sortOrder, reportingEntity: unit.reportingEntity }) }), onSuccess: refresh })
  if (!organization.data || !people.data) return <LoadingCard />
  const active = people.data.filter((person) => person.isActive)
  const error = updateDivision.error ?? updateUnit.error

  return (
    <div className="grid gap-4">
      {error && <p className="text-sm text-destructive">{messageFor(error)}</p>}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">واحدها و مدیر واحد</CardTitle>
          <CardDescription>مدیر واحد، نسبت‌های کاری پرسنل واحد خود را تأیید می‌کند.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead className="text-right">واحد</TableHead><TableHead className="text-right">مدیر واحد</TableHead></TableRow></TableHeader>
            <TableBody>
              {organization.data.divisions.map((division) => (
                <TableRow key={division.id}>
                  <TableCell className="font-medium">{division.nameFa}</TableCell>
                  <TableCell><PersonPicker label={`مدیر ${division.nameFa}`} value={division.headId} people={active} onChange={(headId) => updateDivision.mutate({ ...division, headId })} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">کسب‌وکارها و مدیر کسب‌وکار</CardTitle>
          <CardDescription>مدیر کسب‌وکار سهم کسب‌وکار خود را تأیید نهایی می‌کند و در پنجره سه‌ماهه پیش‌بینی آن را به‌روز می‌کند.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead className="text-right">کسب‌وکار</TableHead><TableHead className="text-right">مدیر کسب‌وکار</TableHead><TableHead className="text-right">گزارش سود و زیان</TableHead></TableRow></TableHeader>
            <TableBody>
              {organization.data.businessUnits.map((unit) => (
                <TableRow key={unit.id}>
                  <TableCell className="font-medium">{unit.nameFa}</TableCell>
                  <TableCell><PersonPicker label={`مدیر ${unit.nameFa}`} value={unit.headId} people={active} onChange={(headId) => updateUnit.mutate({ ...unit, headId })} /></TableCell>
                  <TableCell>
                    <Select value={unit.reportingEntity} onValueChange={(value) => updateUnit.mutate({ ...unit, reportingEntity: value as Unit["reportingEntity"] })}>
                      <SelectTrigger className="h-8 w-48" aria-label={`گزارش سود و زیان ${unit.nameFa}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pishgam">تجمیعی پیشگام</SelectItem>
                        <SelectItem value="faraz">جداگانه (پایش سرمایه فراز)</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}

function PersonPicker({ label, value, people, onChange }: { label: string; value: number | null; people: Person[]; onChange: (id: number | null) => void }) {
  return (
    <Select value={value ? String(value) : "none"} onValueChange={(next) => onChange(next === "none" ? null : Number(next))}>
      <SelectTrigger className="h-8 w-60" aria-label={label}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">— تعیین نشده —</SelectItem>
        {people.map((person) => <SelectItem key={person.id} value={String(person.id)}>{person.nameFa}</SelectItem>)}
      </SelectContent>
    </Select>
  )
}

const FIELDS: Array<{ key: keyof Omit<ProcessSettings, "processStart">; label: string; hint: string }> = [
  { key: "allocationEnd", label: "مهلت ثبت نسبت کاری پرسنل", hint: "از روز ۱ تا این روز ماه" },
  { key: "payrollEnd", label: "مهلت ثبت حقوق و هزینه‌ها (مدیر مالی)", hint: "پس از آن، حقوق و هزینه‌ها از ماه قبل کپی می‌شود" },
  { key: "divisionEnd", label: "مهلت تأیید مدیر واحد", hint: "روز ماه" },
  { key: "unitEnd", label: "مهلت تأیید نهایی مدیر کسب‌وکار", hint: "روز ماه" },
  { key: "closeDay", label: "مهلت بستن دوره", hint: "روز ماه" },
  { key: "forecastWindowDays", label: "طول پنجره بازنگری سه‌ماهه", hint: "روز اول مهر، دی، فروردین و تیر تا این روز" },
]

export function ProcessSettingsAdmin() {
  const client = useQueryClient()
  const settings = useQuery({ queryKey: ["process-settings"], queryFn: () => api<ProcessSettings>("/admin/settings") })
  const [form, setForm] = useState<ProcessSettings | null>(null)
  useEffect(() => { if (settings.data) setForm(settings.data) }, [settings.data])
  const save = useMutation({
    mutationFn: () => api<ProcessSettings>("/admin/settings", { method: "PUT", body: JSON.stringify(form) }),
    onSuccess: () => Promise.all(["process-settings", "process-tasks", "process-status"].map((key) => client.invalidateQueries({ queryKey: [key] }))),
  })
  if (!form) return <LoadingCard />
  return (
    <div className="grid gap-4">
    <Card>
      <CardHeader>
        <CardTitle className="text-base">مهلت‌های فرایند ماهانه</CardTitle>
        <CardDescription>هر ماه حساب ماه قبل بسته می‌شود. روزها، روزهای ماه شمسی جاری هستند.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4 md:grid-cols-2" onSubmit={(event) => { event.preventDefault(); save.mutate() }}>
          {FIELDS.map((field) => (
            <label key={field.key} className="grid gap-1 text-sm">
              <span className="font-medium">{field.label}</span>
              <Input type="number" min={1} max={29} className="w-28" value={form[field.key]} onChange={(event) => setForm({ ...form, [field.key]: Number(event.target.value) })} />
              <span className="text-xs text-muted-foreground">{field.hint}</span>
            </label>
          ))}
          <p className="text-xs text-muted-foreground md:col-span-2">
            فرایند خودکار (یادآوری‌ها، هشدار دیرکرد و کپی حقوق) از بستن حساب {fa(`${["فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور", "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند"][form.processStart.month - 1]} ${form.processStart.year}`)} فعال است.
          </p>
          {save.error && <p className="text-sm text-destructive md:col-span-2">{messageFor(save.error)}</p>}
          {save.isSuccess && !save.isPending && <p className="text-sm text-success md:col-span-2">ذخیره شد.</p>}
          <Button type="submit" className="w-fit" disabled={save.isPending}>ذخیره مهلت‌ها</Button>
        </form>
      </CardContent>
    </Card>
    <ForecastOverrideCard />
    </div>
  )
}

const MONTHS = ["فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور", "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند"]
const ROUNDS: Array<[number, string]> = [[1, "مهر (بودجه سالانه)"], [2, "دی"], [3, "فروردین"], [4, "تیر"]]
type Override = { fiscalYear: number; round: number; until: [number, number, number] }

/** Admin: open forecast editing for a chosen year and round outside the quarterly calendar. */
function ForecastOverrideCard() {
  const client = useQueryClient()
  const tasks = useQuery({ queryKey: ["process-tasks"], queryFn: () => api<TasksResponse>("/process/tasks") })
  const state = useQuery({ queryKey: ["forecast-override"], queryFn: () => api<{ override: Override | null; window: ForecastWindow & { manual: boolean } }>("/admin/forecast-override") })
  const today = tasks.data?.cycle.today
  const [draft, setDraft] = useState<Override | null>(null)
  useEffect(() => {
    if (draft || !today) return
    const fy = today[1] >= 7 ? today[0] : today[0] - 1
    setDraft({ fiscalYear: fy, round: 1, until: [today[0], today[1], Math.min(today[2] + 7, 29)] })
  }, [today, draft])
  const refresh = () => Promise.all(["forecast-override", "process-tasks", "finance-plans"].map((key) => client.invalidateQueries({ queryKey: [key] })))
  const open = useMutation({ mutationFn: () => api("/admin/forecast-override", { method: "PUT", body: JSON.stringify(draft) }), onSuccess: refresh })
  const close = useMutation({ mutationFn: () => api("/admin/forecast-override", { method: "DELETE" }), onSuccess: refresh })
  const window = state.data?.window
  const active = Boolean(window?.manual)
  const roundName = (round: number) => ROUNDS.find(([value]) => value === round)?.[1] ?? fa(round)
  if (!draft || !state.data) return <LoadingCard />
  const set = (patch: Partial<Override>) => setDraft({ ...draft, ...patch })
  const setUntil = (index: number, value: number) => { const until = [...draft.until] as Override["until"]; until[index] = value; set({ until }) }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><CalendarClock className="size-4 text-primary" aria-hidden /> باز کردن دستی پیش‌بینی</CardTitle>
        <CardDescription>خارج از پنجره سه‌ماهه، ویرایش پیش‌بینی یک سال و نسخه را تا تاریخی که تعیین می‌کنید برای مدیران کسب‌وکار باز کنید. با باز شدن، به مدیران کسب‌وکار اعلان می‌رود و در تاریخ پایان خودکار بسته می‌شود.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {active && window && (
          <div role="status" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/40 bg-primary/5 px-4 py-3 text-sm">
            <span><span className="font-medium">باز است:</span> {fiscalYearLabel(window.fiscalYear)}، نسخه {roundName(window.round)}، تا {fa(window.endLabel)}</span>
            <Button variant="outline" size="sm" disabled={close.isPending} onClick={() => close.mutate()}>{close.isPending ? "در حال بستن…" : "بستن"}</Button>
          </div>
        )}
        <form className="flex flex-wrap items-end gap-4" onSubmit={(event) => { event.preventDefault(); open.mutate() }}>
          <label className="grid gap-1 text-sm">
            <span className="font-medium">سال مالی</span>
            <Select value={String(draft.fiscalYear)} onValueChange={(value) => set({ fiscalYear: Number(value) })}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>{[-1, 0, 1].map((offset) => { const year = (today![1] >= 7 ? today![0] : today![0] - 1) + offset; return <SelectItem key={year} value={String(year)}>{fiscalYearLabel(year)}</SelectItem> })}</SelectContent>
            </Select>
          </label>
          <label className="grid gap-1 text-sm">
            <span className="font-medium">نسخه</span>
            <Select value={String(draft.round)} onValueChange={(value) => set({ round: Number(value) })}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>{ROUNDS.map(([value, label]) => <SelectItem key={value} value={String(value)}>{label}</SelectItem>)}</SelectContent>
            </Select>
          </label>
          <fieldset className="grid gap-1 text-sm">
            <legend className="mb-1 font-medium">باز تا (همان روز هم باز است)</legend>
            <div className="flex items-center gap-2">
              <Input type="number" min={1} max={31} className="w-20" aria-label="روز" value={draft.until[2]} onChange={(event) => setUntil(2, Number(event.target.value))} />
              <Select value={String(draft.until[1])} onValueChange={(value) => setUntil(1, Number(value))}>
                <SelectTrigger className="w-32" aria-label="ماه"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((name, index) => <SelectItem key={name} value={String(index + 1)}>{name}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" min={1400} max={1500} className="w-24" aria-label="سال" value={draft.until[0]} onChange={(event) => setUntil(0, Number(event.target.value))} />
            </div>
          </fieldset>
          <Button type="submit" disabled={open.isPending}>{open.isPending ? "در حال باز کردن…" : active ? "به‌روزرسانی" : "باز کردن"}</Button>
        </form>
        {(open.error || close.error) && <p role="alert" className="text-sm text-loss">{messageFor(open.error ?? close.error)}</p>}
        {!active && state.data.override && <p className="text-xs text-muted-foreground">بازه دستی قبلی به پایان رسیده است.</p>}
      </CardContent>
    </Card>
  )
}
